<template>
  <BaseDialog
    :visible="visible"
    @update:visible="(val) => emit('update:visible', val)"
    class="sm:!w-[90%] md:!w-[60%] !max-w-[900px] h-[80vh] sm:h-[85vh] md:h-[90vh] md:rounded-[16px] rounded-[12px]"
    height="auto"
    :draggable="false"
    margin="20px"
  >
    <div class="flex justify-end items-center pt-4 pr-4">
      <button
        @click="emit('update:visible', false)"
        class="cursor-pointer border-none bg-transparent flex justify-center items-center"
        v-html="cancelSvg"
        :aria-label="$t('commonLabels.close')"
        :autofocus="visible ? true : false"
      ></button>
    </div>

    <div class="flex justify-center pb-[32px]">
      <div
        class="z-0 bg-gradient-to-r rounded-full flex items-center justify-center from-warningBgGradient p-3 opacity-25 to-warningBgGradientVia"
        v-html="tooltip({ width: '48', height: '48', color: '#FFFFFF' })"
      ></div>
      <div
        class="z-10 bg-gradient-to-r rounded-full flex items-center justify-center from-mintGradientFrom to-mintGradientTo p-3 ml-[-10px]"
        v-html="
          accountAgentSvg({ width: '48', height: '48', color: '#FFFFFF' })
        "
      ></div>
    </div>

    <div
      class="flex flex-col items-center justify-center mb-[32px] gap-4 px-4 text-center"
    >
      <div>
        <h2
          class="font-just font-400 text-[24px] leading-[100%] tracking-[0%] text-btnActive"
          v-html="title"
        ></h2>
      </div>
      <div class="mt-4">
        <h2
          class="font-just font-400 text-[20px] leading-[100%] tracking-[0%]"
          v-html="subtitle"
        ></h2>
      </div>
    </div>

    <div
      class="mx-[24px] bg-gradient-to-l rounded-[16px] from-agentGradientVia to-agentGradient z-0"
    >
      <div class="flex flex-col lg:flex-row gap-[32px] px-[32px] py-[24px]">
        <div
          class="text-btnActive flex flex-col items-start justify-center gap-4 lg:flex-1 w-full"
        >
          <p
            v-html="note1"
            class="pb-[16px] font-just font-400 text-[16px] leading-[100%] tracking-[0%]"
          ></p>
          <p
            v-if="note2"
            v-html="note2"
            class="font-just font-[600] text-[16px] leading-[100%] tracking-[0%]"
          ></p>
        </div>
        <div
          class="bg-white rounded-[19.12px] p-[24px] max-w-[326px] lg:flex-1 w-full lg:w-auto"
        >
          <div class="flex items-center justify-center relative mb-2">
            <div class="relative">
              <img
                v-if="item?.agentUserId || item?.profileImage"
                :src="
                  item?.profileImage ||
                  `${BaseURL}/objects/${item?.agentUserId}/thumbnail`
                "
                :alt="$t('commonLabels.avatar')"
                class="w-[48px] h-[48px] object-cover rounded-full"
                @error="onErrorImage"
              />
              <img
                v-if="item?.badgeIcon"
                :src="item?.badgeIcon"
                alt="icon"
                class="w-[33px] h-[33px] absolute -right-6 top-1/2 -translate-y-1/2"
              />
            </div>
            <span
              class="font-just ml-8 font-400 text-[16px] text-greyLight"
              v-if="item?.relationship || item?.tag"
            >
              {{ item?.relationship || item?.tag }}
            </span>
          </div>
          <div
            class="font-just font-semibold text-[20px] text-darkBlue text-center w-full"
            v-if="item?.name"
          >
            {{ item?.name }}
          </div>
        </div>
      </div>
    </div>

    <div
      class="gap-[24px] flex items-center flex-col justify-center mx-[24px] mt-[32px] mb-[12px]"
    >
      <div v-if="estatePlanStore.errorMsg">
        <span
          class="text-base my-2 !font-just text-error flex justify-center"
          data-test-id="errorMessage"
        >
          {{ estatePlanStore.errorMsg }}
        </span>
      </div>
      <div class="flex items-center justify-center gap-4 w-full">
        <BaseButton
          :disabled="loading || !!securityStore.errorMsg"
          :loading="loading"
          :label="btnText"
          class="lg:w-1/2 md:lg:w-1/2 sm:w-3/4 w-3/4 !font-just !font-400 !bg-transparent !text-btnActive !border-btnActive !border-solid !border-[1px]"
          @click="generateCode"
        />
        <BaseButton
          v-if="securityStore.errorMsg"
          :label="$t('commonLabels.enterPassphrase')"
          class="w-1/2 !font-just !font-400 !bg-transparent !text-btnActive !border-btnActive !border-solid !border-[1px]"
          @click="goToAuth"
        />
      </div>
    </div>
    <div class="flex items-center justify-center mx-6 mt-4 mb-8">
      <button
        @click="emit('update:visible', false)"
        :aria-label="$t('documentLabels.maybeLater')"
        class="bg-transparent border-none underline cursor-pointer font-just font-400 text-[16px] leading-[24px] tracking-[-0.2%]"
      >
        {{ $t('documentLabels.maybeLater') }}
      </button>
    </div>
  </BaseDialog>
</template>

<script setup>
import { ref, computed } from "vue";
import { closeSvg, tooltip, accountAgentSvg } from "@/utils/commonSvg";
import { useEstatePlanStore } from "@/stores/estatePlanStore";
import { ROUTE_PATHS } from "@/router/constants";
import router from "@/router";
import { useSecurityStore } from "@/stores/securityStore";
import { BaseURL } from "@/api/apiService";
import { onErrorImage } from "@/utils/common";

const estatePlanStore = useEstatePlanStore();
const securityStore = useSecurityStore();
const fullscreen = ref(true);
const cancelSvg = computed(() =>
  closeSvg({
    height: fullscreen.value ? "32px" : "24px",
    width: fullscreen.value ? "32px" : "24px",
  })
);

const emit = defineEmits([
  "update:visible",
  "generateNewCode",
  "generateCode",
  "goToAuthenticateCode",
]);

const props = defineProps({
  mode: String,
  btnText: String,
  title: String,
  subtitle: String,
  note1: String,
  note2: String,
  modelValue: Boolean,
  showCancel: {
    type: Boolean,
    default: true,
  },
  visible: { type: Boolean, required: true },
  item: { required: true },
  loading: Boolean,
});

const generateCode = () => {
  if (props.mode === "authenticate") {
    emit("goToAuthenticateCode", props.item);
  } else emit("generateCode", props.item);
};

const goToAuth = async () => {
  router.push(ROUTE_PATHS.SECURITY);
  estatePlanStore.isAgentAuthenticateModalOpen = false;
};
</script>
